import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';

import LoginContainer from 'containers/LoginContainer';
import LoginInvitationDialog from 'containers/LoginPage/components/LoginInvitationDialog';
import LoginAccountCreation from 'containers/LoginPage/containers/LoginAccountCreation';
import LoginCodeVerification from 'containers/LoginPage/containers/LoginCodeVerification';
import LoginForgotPassword from 'containers/LoginPage/containers/LoginForgotPassword';
import LoginPassword from 'containers/LoginPage/containers/LoginPassword';
import LoginWelcome from 'containers/LoginPage/containers/LoginWelcome';
import useAuthSession from 'hooks/useAuthSession';
import useCurrentUser from 'hooks/useCurrentUser';
import useGlobalErrorHandler from 'hooks/useGlobalErrorHandler';
import { Routing } from 'routing/routing';
import globalMessages from 'translations/messages/global-messages';
import sessionStorageUser from 'utils/sessionStorageUser';

const LoginPage = () => {
  // #region [intl]
  const intl = useIntl();
  // #endregion

  // #region [auth]
  const { email: currentUserEmail } = useCurrentUser();
  const { isAuthenticated, isLoading, clearLocalState, logout } = useAuthSession();
  const dispatchGlobalError = useGlobalErrorHandler();
  // #endregion

  // #region [router]
  const history = useHistory();
  const location = useLocation();
  // #endregion

  // #region [states]
  const [userInfo, setUserInfo] = useState(null);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [isIDPFlow, setIsIDPFlow] = useState(false);
  const [showInvitationDialog, setShowInvitationDialog] = useState(false);
  // #endregion

  // #region [memos]
  const invitationParams = useMemo(
    () => {
      const params = new URLSearchParams(location.search);
      if (params.get('action') !== 'Invitation') return null;

      const email = params.get('email');
      if (!email) return null;

      return {
        email: decodeURIComponent(email),
        organizationId: params.get('organization_id'),
        organizationName: params.get('organization_name'),
        projectId: params.get('project_id'),
        projectName: params.get('project_name'),
        callbackUrl: params.get('callback_url'),
      };
    },
    [location.search],
  );

  const codeParams = useMemo(
    () => {
      const params = new URLSearchParams(location.search);
      if (params.get('action') !== 'EmailValidation') return null;

      const email = params.get('email');
      const code = params.get('code');
      if (!email || !code) return null;

      return {
        email: decodeURIComponent(email),
        code: code.startsWith('F-') ? code.slice(2) : code,
      };
    },
    [location.search],
  );
  // #endregion

  // #region [refs]
  const isIDPFlowRef = useRef(false);
  // #endregion

  // #region [effects]
  useEffect(() => {
    const loginCallbackError = sessionStorage.getItem('loginCallbackError');
    if (!loginCallbackError) return;
    sessionStorage.removeItem('loginCallbackError');
    try {
      const error = JSON.parse(loginCallbackError);
      dispatchGlobalError({
        error,
        codeToMessageIntl: {
          account_exists_different_provider: globalMessages.account_exists_different_provider,
          authentication_unknown_user: globalMessages.authentication_unknown_user,
        },
      });
    } catch (err) {
      dispatchGlobalError({ error: err });
    }
  }, []);

  useEffect(
    () => {
      if (!invitationParams) return;

      if (invitationParams.callbackUrl) {
        sessionStorageUser.setDeepLink({
          url: invitationParams.callbackUrl,
          organizationId: invitationParams.organizationId,
          projectId: invitationParams.projectId,
          intendedEmail: invitationParams.email,
        });
      }

      if (!isLoading && isAuthenticated) {
        if (currentUserEmail === invitationParams.email) {
          history.replace(
            Routing.home.home(invitationParams.organizationId, invitationParams.projectId),
          );
        } else {
          setShowInvitationDialog(true);
        }
      }
    },
    [invitationParams, isLoading, isAuthenticated, currentUserEmail, history],
  );

  useEffect(() => {
    const postLogoutSearch = sessionStorage.getItem('pendingInvitationParams');
    if (postLogoutSearch) {
      sessionStorage.removeItem('pendingInvitationParams');
      history.replace(`/login${postLogoutSearch}`);
      return;
    }

    if (invitationParams || codeParams) return;

    const loginCallbackFlow = sessionStorage.getItem('loginCallbackFlow');
    if (!loginCallbackFlow) {
      if (!isAuthenticated) clearLocalState();
      return;
    }

    sessionStorage.removeItem('loginCallbackFlow');
    isIDPFlowRef.current = true;
    setIsIDPFlow(true);

    try {
      const { userInfo: storedUserInfo, expiresAt } = JSON.parse(loginCallbackFlow);
      if (expiresAt > Date.now()) setUserInfo(storedUserInfo);
    } catch (err) {
      console.error('Invalid login callback flow payload', err);
    }
  }, []);

  useEffect(
    () => {
      if (isIDPFlowRef.current || invitationParams || codeParams || isLoading) return;
      if (isAuthenticated) history.replace('/');
    },
    [isAuthenticated, isLoading, invitationParams, codeParams, history],
  );
  // #endregion

  // #region [methods]
  const handleInvitationDialogSubmit = async () => {
    sessionStorage.setItem('pendingInvitationParams', location.search);
    await logout();
  };

  const handleBackButtonClick = () => {
    setUserInfo(null);
    setForgotPassword(false);
    if (invitationParams || codeParams) history.replace('/login');
  };

  const handleCodeValidate = userInfo => {
    setUserInfo(userInfo);
    if (codeParams) {
      isIDPFlowRef.current = true;
      if (isAuthenticated) setIsIDPFlow(true);
      history.replace('/login');
    }
  };

  const handleAuthenticate = newUserInfo => {
    setUserInfo(newUserInfo);
    if (invitationParams) history.replace('/login');
  };
  // #endregion

  // #region [render]
  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage(globalMessages.finalcad_login_or_signup)} |{' '}
          {intl.formatMessage(globalMessages.finalcad)}
        </title>
      </Helmet>
      {showInvitationDialog ? (
        <LoginInvitationDialog
          email={invitationParams.email}
          onSubmit={handleInvitationDialogSubmit}
        />
      ) : (
        <LoginContainer>
          {(() => {
            if (codeParams) {
              return (
                <LoginCodeVerification
                  email={codeParams.email}
                  code={codeParams.code}
                  firstName={userInfo?.firstName || ''}
                  lastName={userInfo?.lastName || ''}
                  onCodeValidate={handleCodeValidate}
                  onBackButtonClick={handleBackButtonClick}
                />
              );
            }
            if (userInfo) {
              if (userInfo.email_validation_required && !userInfo.validationCode) {
                return (
                  <LoginCodeVerification
                    email={userInfo.email}
                    firstName={userInfo.firstName}
                    lastName={userInfo.lastName}
                    onCodeValidate={handleCodeValidate}
                    onBackButtonClick={handleBackButtonClick}
                  />
                );
              }
              if (userInfo.invitations?.length || !userInfo.exists) {
                return (
                  <LoginAccountCreation
                    initialFirstName={userInfo.firstName}
                    initialLastName={userInfo.lastName}
                    email={userInfo.email}
                    isIDPFlow={isIDPFlow}
                    validationCode={userInfo.validationCode}
                    onBackButtonClick={handleBackButtonClick}
                  />
                );
              }
              if (
                userInfo.exists &&
                (!userInfo.email_validation_required || userInfo.validationCode)
              ) {
                return forgotPassword ? (
                  <LoginForgotPassword
                    email={userInfo.email}
                    onBackButtonClick={handleBackButtonClick}
                  />
                ) : (
                  <LoginPassword
                    email={userInfo.email}
                    onBackButtonClick={handleBackButtonClick}
                    onForgotPasswordClick={() => setForgotPassword(true)}
                  />
                );
              }
            }
            return (
              <LoginWelcome
                invitationEmail={invitationParams?.email}
                onAuthenticate={handleAuthenticate}
              />
            );
          })()}
        </LoginContainer>
      )}
    </>
  );
  // #endregion
};

export default LoginPage;
